import clsx from 'clsx';
import type { MouseEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinGetDataFunction,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import type { AddFuncParameters } from '../../../common/types/add-function-parameters';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import ContinuousScroller from '../../continuous-scroller/continuous-scroller.component';
import type { JsonData, JsonDataItem } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import type { OdinIconName, OdinIconSize } from '../../odin-icon/odin-icon.component';
import OdinIcon from '../../odin-icon/odin-icon.component';
import type { IconSelectorProps } from './icon-selector.props';

const IconSelector = ({
  id,
  name,
  label,
  value,
  data,
  valueField = 'icon',
  textField = 'iconFileName',
  readOnly,
  disabled,
  required,
  error,
  onChange,
  className,
}: IconSelectorProps) => {
  const useStaticData = Array.isArray(data);

  const [selectedValue, setSelectedValue] = useState<string>();
  const [scrollElement, setScrollElement] = useState<Element>();

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleOnChange: AddFuncParameters<MouseEventHandler<HTMLDivElement>, [icon: string]> = (event, icon) => {
    event.preventDefault();
    event.stopPropagation();

    setSelectedValue(icon);
    onChange?.(icon);
  };

  const renderIcon = (item: JsonDataItem, index: number) => {
    const iconClasses = clsx(
      'odin-grid odin-place-content-center odin-py-2 odin-cursor-pointer odin-border odin-border-transparent hover:odin-border-primary-1',
      {
        'odin-border odin-border-primary-1 odin-bg-primary-4': selectedValue === item[valueField],
      }
    );

    return (
      <div
        key={`${name}-icon-${index}`}
        title={item[valueField]?.toString()}
        className={iconClasses}
        onClick={(event) => handleOnChange(event, item[valueField] as string)}
      >
        <OdinIcon icon={item[textField] as OdinIconName} size={item['size'] as OdinIconSize} />
      </div>
    );
  };

  const iconContainerClasses = 'odin-grid odin-grid-cols-min-42 odin-max-h-[200px] odin-overflow-auto';

  const renderIcons = (finalData: JsonData) => (
    <div className={iconContainerClasses} tabIndex={readOnly || disabled ? -1 : 0}>
      {finalData.map(renderIcon)}
    </div>
  );

  const getData = useCallback<OdinGetDataFunction>(
    (dataSender: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (data && !useStaticData) {
        const dataOptions: OdinDataRetrievalOptions = {
          ...options,
          fieldId: id,
          fieldType: 'ICON_SELECTOR',
        };

        data.getData(dataSender, dataOptions);
      }
    },
    [data, id, useStaticData]
  );

  const dataRetriever = useMemo<OdinDataRetrieval>(() => {
    return { getData };
  }, [getData]);

  const onScrollElementCreated = (element: HTMLDivElement) => {
    setScrollElement(element);
  };

  return (
    <div className={className} ref={(element) => element && onScrollElementCreated(element)}>
      {label && (
        <div className="odin-flex">
          <FieldLabel label={label} name={name} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {useStaticData ? (
        renderIcons(data)
      ) : (
        <ContinuousScroller dataRetriever={dataRetriever} scrollingElement={scrollElement}>
          {renderIcons}
        </ContinuousScroller>
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};

export { IconSelector };
